import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import setRef from 'hooks/setRef'
import useForkRef from 'hooks/useForkRef'
import { validHTMLElementType } from 'settings/propTypeDefinitions'

function getContainer(container) {
  return typeof container === 'function' ? container() : container
}

const useEnchancedEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

const Portal = React.forwardRef((props, ref) => {
  const { children, container, disablePortal = false, onRendered } = props
  const [mountNode, setMountedNode] = React.useState(null)
  const handleRef = useForkRef(
    React.isValidElement(children) ? children.ref : null,
    ref
  )

  useEnchancedEffect(() => {
    if (!disablePortal) {
      setMountedNode(getContainer(container) || document.body)
    }
  }, [container, disablePortal])

  useEnchancedEffect(() => {
    if (mountNode && !disablePortal) {
      setRef(ref, mountNode)
      return () => {
        setRef(ref, null)
      }
    }

    return undefined
  }, [ref, mountNode, disablePortal])

  useEnchancedEffect(() => {
    if (onRendered && (mountNode || disablePortal)) {
      onRendered()
    }
  }, [onRendered, mountNode, disablePortal])

  if (disablePortal) {
    if (React.isValidElement(children)) {
      return React.cloneElement(children, {
        ref: handleRef,
      })
    }
    return children
  }

  return mountNode ? ReactDOM.createPortal(children, mountNode) : mountNode
})

Portal.propTypes = {
  // The children to render into the `container`
  children: PropTypes.node,
  // By default, it uses the body of the top-level document object,
  // so it's simply `document.body` most of the time
  container: PropTypes.oneOfType([validHTMLElementType, PropTypes.func]),
  // The `children` will be inside the DOM hierarchy of the parent component
  disablePortal: PropTypes.bool,
  // Callback fired once the children has been mounted into the `container`
  onRendered: PropTypes.func,
}

export default Portal
